import React from "react";
import { Shirt, Stamp, Users, GraduationCap, Gift, Search, PackageCheck } from "lucide-react";
import { IMAGES } from "../lib/site";
import { Reveal } from "../components/Reveal";
import { PageHero, SectionHeader, CTAButton, useSeo } from "../components/Shared";

const SECTIONS = [
  { icon: Shirt, t: "In-House Apparel Production", d: "Apparel produced with your brand front and center — for teams, events, and everyday wear." },
  { icon: Stamp, t: "Screen Printing & Embroidery", d: "Decoration services that put your logo and identity on the products your audience will actually use." },
  { icon: Users, t: "Uniforms & Team Apparel", d: "Consistent, professional apparel programs for staff across roles and locations." },
  { icon: GraduationCap, t: "Employee, Recruiting & Onboarding Programs", d: "Welcome kits, recruiting giveaways, and onboarding merchandise that make a strong first impression." },
  { icon: Gift, t: "Branded Gifts & Event Giveaways", d: "Memorable gifts and giveaways selected to fit your audience, occasion, and budget." },
  { icon: Search, t: "Product Sourcing & Consultation", d: "Guidance on selecting the right products for your audience, quantity, budget, and timeline." },
  { icon: PackageCheck, t: "Kitting, Storage, Fulfillment & Distribution", d: "Once produced, your merchandise can be stored, kitted, and shipped through MJG fulfillment." },
];

export default function Promotional() {
  useSeo(
    "Apparel & Promotional Products | MJG Promotions",
    "Custom apparel, embroidery, promotional products, uniforms, and branded giveaways in New Jersey — selected, produced, stored, and fulfilled by MJG Promotions."
  );
  return (
    <>
      <PageHero
        eyebrow="Apparel & Promotional Products"
        title="Apparel and Promotional Products That Carry Your Brand"
        intro="From uniforms and team apparel to gifts, kits, and event giveaways, MJG helps select, produce, organize, and deliver branded merchandise for every audience."
        image={IMAGES.apparelRack}
      />

      <section className="bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5">
          <SectionHeader eyebrow="Capabilities" title="Made, Managed, and Delivered" body="MJG combines in-house apparel production with experienced promotional-product support — then connects it to storage and fulfillment so your program keeps running." />
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {SECTIONS.map((s, i) => (
              <Reveal key={s.t} delay={(i % 3) * 0.06}>
                <div className="group h-full rounded-sm border border-brand-line bg-brand-warm p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-brand-red hover:bg-white hover:shadow-xl">
                  <s.icon className="h-7 w-7 text-brand-red" aria-hidden="true" />
                  <h3 className="mt-5 text-lg font-bold text-brand-black">{s.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-brand-gray">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-warm py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <Reveal>
              <img src={IMAGES.merchFlatlay} alt="Branded merchandise selection including apparel, drinkware, and bags" className="w-full rounded-sm object-cover" loading="lazy" />
            </Reveal>
            <Reveal delay={0.1}>
              <SectionHeader eyebrow="Start a Project" title="Tell Us What You Need" body="Share your audience, quantity, budget, need-by date, and delivery locations. We'll help you source and build a program that fits — no full catalog to dig through." />
              <ul className="mt-6 space-y-2 text-sm text-brand-charcoal">
                <li>• Audience and use case</li>
                <li>• Estimated quantity and budget</li>
                <li>• Need-by date</li>
                <li>• Delivery locations</li>
              </ul>
              <CTAButton to="/contact" testId="promo-cta" variant="primary">Start a Merchandise Project</CTAButton>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
